import { SVGInternalIcon } from "@components:SVGInternalIcon";
import {
  Box,
  Divider,
  IconButton,
  keyframes,
  SxProps,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { SuperButton } from "@pages:app/docs/Components/SuperButton";
import prettyBytes from "pretty-bytes";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdExpandLess, MdExpandMore } from "react-icons/md";

type UploadButtonProps = {
  file: File | undefined;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  fileLabel: string;
  accept: string;
  sx?: SxProps;
};

const boundKeyframes = keyframes(
  `
  0%, 100% {
    transform: translateY(-25%);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }
  50% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
  `,
);

export const UploadButton = ({
  file,
  onFileChange,
  fileLabel,
  accept,
  sx,
}: UploadButtonProps) => {
  const { t } = useTranslation();
  const [showFileDetail, setShowFileDetail] = useState(false);

  return (
    <SuperButton
      sx={{
        display: "flex",
        flexDirection: "column",
        height:
          showFileDetail && !!file ? "240px !important" : "170px !important",
        ...sx,
      }}
      isShownPingIcon={!!file}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="100%"
      >
        <input
          data-cy={"input-file-upload-docs"}
          type="file"
          hidden
          accept={accept}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            onFileChange(e);
            // FIX upload same file not working
            // https://stackoverflow.com/questions/26634616/filereader-upload-same-file-again-not-working
            e.target.value = "";
          }}
        />
        <Box display="flex" alignItems="center" gap={2}>
          <SVGInternalIcon icon="uploadIcon" sx={{ width: 32, height: 32 }} />
          <Typography
            sx={{
              fontWeight: "500",
              fontSize: "20px",
              lineHeight: "26px",
            }}
          >
            {t("common.upload")}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ marginTop: "35px" }}
          >
            <Typography
              sx={{
                textAlign: "left",
                fontWeight: "500",
                fontSize: "16px",
                lineHeight: "26px",
              }}
            >
              {fileLabel}
            </Typography>
            {!!file &&
              (showFileDetail ? (
                <Tooltip title={t("docs.hideFileDetail")} placement="top">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowFileDetail(false);
                    }}
                    sx={{
                      padding: 0,
                    }}
                  >
                    <MdExpandLess />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t("docs.showFileDetail")} placement="top">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowFileDetail(true);
                    }}
                    sx={{
                      padding: 0,
                      animation: `${boundKeyframes} 1s infinite`,
                    }}
                  >
                    <MdExpandMore />
                  </IconButton>
                </Tooltip>
              ))}
          </Box>
          {!!file && showFileDetail && (
            <Box>
              <Divider
                sx={{
                  paddingY: "0.5rem",
                }}
              />
              <Box display="flex" gap={2} alignItems="center">
                <SVGInternalIcon
                  icon="documentIcon"
                  sx={{
                    width: 32,
                    height: 32,
                    marginTop: "0.5rem",
                    color: "#21ADB2",
                  }}
                />
                <Box>
                  <Tooltip title={file?.name} placement="top">
                    <Box
                      sx={{
                        width: "12.5rem",
                      }}
                    >
                      <Typography
                        sx={{
                          textAlign: "left",
                          marginTop: "0.5rem",
                        }}
                        noWrap
                      >
                        {file?.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Typography
                    sx={{
                      textAlign: "left",
                      marginTop: "0.5rem",
                    }}
                  >
                    {prettyBytes(file?.size)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </SuperButton>
  );
};
